type GraphQLErrorResponse = {
  message: string;
  path?: readonly (string | number)[];
};

export const isHandledAgencyLifecycleRefusal = (
  operationName: string,
  error: GraphQLErrorResponse,
): boolean =>
  (operationName === "TransferAgencyTalent" &&
    error.path?.[0] === "transferAgencyTalent" &&
    [
      "Transfer preview changed. Review the people and obligations again",
      "Representation context changed. Review again before continuing",
      "This request key was already used for different inputs or another actor",
    ].includes(error.message)) ||
  (operationName === "AgencyTransferResult" &&
    error.path?.[0] === "agencyRosterTransfer" &&
    error.message === "Transfer result is not available");

// The company settings page displays this expected refusal inline.
export const isHandledBillingCompanyVatRefusal = (
  operationName: string,
  error: GraphQLErrorResponse,
): boolean =>
  operationName === "SetDefaultBillingCompany" &&
  error.path?.[0] === "setDefaultBillingCompany" &&
  error.message ===
    "VAT number must be 9 or 12 digits, optionally starting GB or XI";

export const isHandledCreateUserDuplicate = (
  operationName: string,
  error: GraphQLErrorResponse,
): boolean =>
  operationName === "CreateUser" &&
  error.path?.[0] === "createUser" &&
  error.message === "Email has already been taken";

// Each operation that can carry the refusal, with the field that raises it.
const CHAT_REFUSAL_FIELDS: Record<string, string> = {
  CreateMessage: "addMessageToChatroom",
  CreateChatroom: "createChatroom",
  CreateBroadcast: "createBroadcast",
};

export const isHandledChatRefusal = (
  operationName: string,
  error: GraphQLErrorResponse,
): boolean =>
  CHAT_REFUSAL_FIELDS[operationName] !== undefined &&
  error.path?.[0] === CHAT_REFUSAL_FIELDS[operationName] &&
  error.message ===
    "Contact coordinates client messages until talent is booked";

// The fixed refusals of Payments::InvoiceReissuer and setInvoiceBillingCompany.
// The invoices list's Reissue invoice dialog, the job's Invoicing Details and
// the bulk review show them where staff acted.
export const REISSUE_PREVIEW_REQUIRED =
  "Reload this page before reissuing this invoice.";
export const REISSUE_PREVIEW_STALE =
  "This invoice has changed. Reopen it and review the reissue again.";

export const isStaleReissuePreview = (message?: string) =>
  message === REISSUE_PREVIEW_REQUIRED || message === REISSUE_PREVIEW_STALE;

const REISSUE_REFUSALS = [
  "Invoices that are paid, refunded or mid-payment cannot be reissued",
  "Invoices with payments cannot be reissued",
  "Invoices with a Trustshare payment reference cannot be reissued",
  "Invoices issued before billing companies existed cannot be reissued",
  "This invoice is already issued with these details",
  "Fix the selected company's VAT number in Company settings before issuing invoices.",
  REISSUE_PREVIEW_REQUIRED,
  REISSUE_PREVIEW_STALE,
];

// setInvoiceBillingCompany's refusal for an invoice issued since the page
// loaded.
export const ISSUED_INVOICE_COMPANY_REFUSAL =
  "Issued invoices cannot change billing company";

export const isHandledReissueRefusal = (
  operationName: string,
  error: GraphQLErrorResponse,
): boolean =>
  (operationName === "ReissueInvoice" &&
    error.path?.[0] === "reissueInvoice" &&
    (REISSUE_REFUSALS.includes(error.message) ||
      error.message.startsWith("This invoice is already with "))) ||
  (operationName === "SetInvoiceBillingCompany" &&
    error.path?.[0] === "setInvoiceBillingCompany" &&
    (error.message === ISSUED_INVOICE_COMPANY_REFUSAL ||
      error.message ===
        "Fix the selected company's VAT number in Company settings before issuing invoices."));
